import { useInfiniteQuery } from "@tanstack/react-query"
import { useEffect, useMemo } from "react"
import { useAuth } from "~/features/auth/useAuth"
import { isEbookEdge } from "~/features/library/guards"
import type { EbookAsset } from "~/features/library/types"
import { GetEbookLibraryDocument } from "~/graphql/generated/graphql"
import { useGraphQLClient } from "~/shared/api/GraphQLClientContext"
import { nextLibraryCursor } from "~/shared/lib/pagination"

/**
 * The same safety stop `useEntitlements` takes, for the same reason: a facade or
 * a graph that never clears `hasNextPage` must not spin forever.
 */
const MAX_PAGES = 10

/**
 * Every ebook, fetched without being asked.
 *
 * Deliberately NOT `useLibraryQuery`. That hook backs the Ebooks section, which
 * pages lazily behind Load more — so an index built on it would be incomplete by
 * construction, and a study's material would blink into existence only once the
 * customer had scrolled an unrelated section far enough. It also keeps its own
 * query key so draining here cannot change what that section renders.
 */
export const useEbookIndexSource = (): { readonly assets: readonly EbookAsset[]; readonly isLoading: boolean } => {
  const client = useGraphQLClient()
  const { status } = useAuth()

  const query = useInfiniteQuery({
    queryKey: ["studyEbook", "ebooks"],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      client.request(GetEbookLibraryDocument, { cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextLibraryCursor,
    enabled: status === "signedIn",
  })

  const isCapped = (query.data?.pages.length ?? 0) >= MAX_PAGES

  useEffect(() => {
    if (query.hasNextPage && !query.isFetchingNextPage && !isCapped) {
      query.fetchNextPage()
    }
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage, isCapped])

  const assets = useMemo(
    () =>
      (query.data?.pages ?? [])
        .flatMap(page => page.user?.libraryV2?.edges ?? [])
        .filter(isEbookEdge)
        .map(edge => edge.node.asset),
    [query.data?.pages],
  )

  return { assets, isLoading: query.isLoading || (Boolean(query.hasNextPage) && !isCapped) }
}
